"use client";

import { useMemo } from "react";
import AuthSignInButton from "@/components/AuthSignInButton";
import { useFollows } from "@/hooks/useFollows";
import { useFollowsStore } from "@/stores/followsStore";
import { useAuthContext } from "@/context/authContext";

import styles from "./styles.module.scss";
import { UnstyledButton as MantineUnstyledButton } from "@mantine/core";

type FollowButtonProps = {
  type: "brand" | "vendor";
  id: number | string | undefined | null;
  name?: string;
  className?: string;
  size?: "small" | "medium" | "large";
  brand?: BrandFilter;
  vendor?: Vendor;
};

export default function FollowButton({
  type,
  id,
  name,
  className,
  size = "medium",
  brand,
  vendor,
}: FollowButtonProps) {
  const { isSignedIn } = useAuthContext();

  useFollows();
  const {
    followedBrands,
    followedVendors,
    loading: followLoading,
    hasLoaded,
  } = useFollowsStore((state) => state);
  const {
    optimisticallyFollowBrand,
    optimisticallyUnfollowBrand,
    optimisticallyFollowVendor,
    optimisticallyUnfollowVendor,
  } = useFollowsStore((state) => state.actions);

  const entityId = useMemo(() => {
    if (!id) return null;
    const parsed = parseInt(String(id), 10);
    return isNaN(parsed) ? null : parsed;
  }, [id]);

  const isFollowed = useMemo(() => {
    if (entityId === null) return false;
    if (type === "brand") {
      return followedBrands.some((b) => b.id === entityId);
    }
    return followedVendors.some((v) => v.id === entityId);
  }, [entityId, type, followedBrands, followedVendors]);

  // Generate button text
  const buttonText = useMemo(() => {
    if (isFollowed) {
      return "Following";
    }
    if (name) {
      return `Follow ${name}`;
    }
    return "Follow";
  }, [isFollowed, name]);

  const handleFollow = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isSignedIn || entityId === null) {
      return;
    }

    try {
      if (type === "brand") {
        if (isFollowed) {
          await optimisticallyUnfollowBrand(entityId);
        } else {
          await optimisticallyFollowBrand(entityId, brand);
        }
      } else if (type === "vendor") {
        if (isFollowed) {
          await optimisticallyUnfollowVendor(entityId);
        } else {
          await optimisticallyFollowVendor(entityId, vendor);
        }
      }
    } catch (error) {
      console.error(`Error toggling ${type} follow:`, error);
    }
  };

  // Don't show button until follows data is loaded (prevents flash)
  if (entityId === null || (isSignedIn && !hasLoaded)) {
    return null;
  }

  // Show sign-in button for non-authenticated users
  if (!isSignedIn) {
    return (
      <AuthSignInButton>
        {/* eslint-disable-next-line no-restricted-syntax -- Clerk must clone a native element. */}
        <button
          className={`${styles.followButton} ${className || ""}`}
          data-following={false}
          data-size={size}
          onClick={(e) => e.stopPropagation()}
        >
          {buttonText}
        </button>
      </AuthSignInButton>
    );
  }

  return (
    <MantineUnstyledButton
      onClick={handleFollow}
      disabled={followLoading}
      className={`${styles.followButton} ${className || ""}`}
      data-following={isFollowed}
      data-size={size}
    >
      {buttonText}
    </MantineUnstyledButton>
  );
}
